/**
 * Design Tokens
 * Centralized design system tokens for spacing, typography, colors, and more.
 */

// Spacing scale (4px base unit)
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
  '4xl': 40,
  '5xl': 48,
  '6xl': 64,
  '7xl': 80,
  '8xl': 96,
} as const;

// Typography scale
export const fontSizes = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
  '5xl': 48,
} as const;

export const lineHeights = {
  xs: 1.4,
  sm: 1.5,
  md: 1.6,
  lg: 1.7,
  xl: 1.8,
} as const;

export const fontWeights = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// Border radius scale
export const radius = {
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  round: '50%',
} as const;

// Shadow scale (base - will be adapted for light/dark in theme)
export const shadows = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
} as const;

// Theme-aware shadows (light mode)
export const shadowsLight = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
} as const;

// Theme-aware shadows (dark mode - more pronounced)
export const shadowsDark = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.4)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
} as const;

// Z-index scale
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  notification: 1080,
} as const;

// Transition durations (in milliseconds)
export const transitions = {
  fast: 150,
  normal: 200,
  slow: 300,
  slower: 500,
} as const;

// Transition easings
export const easings = {
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
} as const;

// Semantic colors (will be extended in theme)
export const semanticColors = {
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
} as const;

// Opacity scale for consistent transparency usage
export const opacity = {
  transparent: 0,
  subtle: 0.05,
  light: 0.1,
  medium: 0.15,
  moderate: 0.25,
  semi: 0.5,
  strong: 0.75,
  opaque: 1,
} as const;

// Border colors for semantic states
export const borderColors = {
  default: 'var(--mantine-color-gray-3)',
  hover: 'var(--mantine-color-gray-4)',
  focus: 'var(--mantine-color-violet-5)',
  error: 'var(--mantine-color-error-5)',
  success: 'var(--mantine-color-success-5)',
  warning: 'var(--mantine-color-warning-5)',
  info: 'var(--mantine-color-info-5)',
} as const;

// Overlay colors for modals, drawers, etc.
export const overlayColors = {
  light: 'rgba(255, 255, 255, 0.8)',
  dark: 'rgba(0, 0, 0, 0.5)',
  blur: 'rgba(0, 0, 0, 0.3)',
} as const;

// Gradient definitions (theme-aware)
export const gradients = {
  primary: {
    light: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    dark: 'linear-gradient(135deg, #5d4e96 0%, #4a3d78 100%)',
  },
  success: {
    light: 'linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(34, 197, 94, 0.08) 100%)',
    dark: 'linear-gradient(135deg, rgba(34, 197, 94, 0.25) 0%, rgba(34, 197, 94, 0.15) 100%)',
  },
  error: {
    light: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(239, 68, 68, 0.08) 100%)',
    dark: 'linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(239, 68, 68, 0.15) 100%)',
  },
  subtle: {
    light: 'linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.01) 100%)',
    dark: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
  },
} as const;

// Icon sizes
export const iconSizes = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  '2xl': 40,
} as const;

// Breakpoints (for responsive design)
export const breakpoints = {
  xs: '36em', // 576px
  sm: '48em', // 768px
  md: '62em', // 992px
  lg: '75em', // 1200px
  xl: '88em', // 1408px
} as const;

// Export all tokens as a single object for convenience
export const tokens = {
  spacing,
  fontSizes,
  lineHeights,
  fontWeights,
  radius,
  shadows,
  shadowsLight,
  shadowsDark,
  zIndex,
  transitions,
  easings,
  semanticColors,
  opacity,
  borderColors,
  overlayColors,
  gradients,
  iconSizes,
  breakpoints,
} as const;

export type Spacing = keyof typeof spacing;
export type FontSize = keyof typeof fontSizes;
export type Radius = keyof typeof radius;
export type Shadow = keyof typeof shadows;
export type IconSize = keyof typeof iconSizes;
export type Opacity = keyof typeof opacity;
export type Gradient = keyof typeof gradients;

